import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const ITEMS_PER_PAGE = 25;

function paginate<T>(
  data: T[],
  itemsPerPage: number,
  page: number,
): { totalPages: number; paginatedData: T[] } {
  if (data.length <= itemsPerPage) {
    return { totalPages: 1, paginatedData: data };
  }
  const end = page * itemsPerPage;
  const start = end - itemsPerPage;
  const totalPages = Math.ceil(data.length / itemsPerPage);
  return { totalPages, paginatedData: data.slice(start, end) };
}

const usePaginateFilters = (rowsPerPage = ITEMS_PER_PAGE) => {
  const [params, setParams] = useSearchParams();
  const pageParam = params.get("page");
  const rowsParam = params.get("rows");
  const page = pageParam ? Number.parseInt(pageParam, 10) : 1;
  const itemsPerPage = rowsParam ? Number.parseInt(rowsParam, 10) : rowsPerPage;

  // Keep any params that are already there
  const setPage = useCallback(
    (newPage: number) => {
      if (newPage === 0) {
        return;
      }
      setParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("page", newPage.toString());
          return newParams;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const resetPageParam = useCallback(() => {
    setParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("page", "1");
        return newParams;
      },
      { replace: true },
    );
  }, [setParams]);

  const setItemsPerPage = useCallback(
    (items: number) => {
      setParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("rows", items.toString());
          return newParams;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return {
    setPage,
    page,
    resetPageParam,
    setItemsPerPage,
    itemsPerPage,
  };
};

export interface PaginateProps<T> {
  isLoading?: boolean;
  page: number;
  totalPages: number;
  totalItems: number;
  data: T[];
  itemsPerPage: number;
  setPage: (p: number) => void;
  setItemsPerPage: (rows: number) => void;
  prev: () => void;
  next: () => void;
}

export function usePaginate<T>(
  data: T[],
  rowsPerPage?: number,
): PaginateProps<T> {
  const { page, setPage, itemsPerPage, setItemsPerPage } =
    usePaginateFilters(rowsPerPage);

  // Calculate total pages first to validate the current page
  const totalPages =
    data.length <= itemsPerPage ? 1 : Math.ceil(data.length / itemsPerPage);

  // Only clamp page if we have data, otherwise preserve the URL page
  const validPage =
    data.length > 0 ? Math.min(Math.max(1, page), totalPages) : page;

  // Use the valid page for pagination
  const { paginatedData } = paginate(data, itemsPerPage, validPage);
  const totalItems = data.length;

  // If the page in URL exceeds total pages, correct it (but only if we have data)
  useEffect(() => {
    if (data.length > 0 && validPage !== page) {
      setPage(validPage);
    }
  }, [validPage, page, setPage, data.length]);

  return {
    page: validPage,
    setPage,
    totalPages,
    totalItems,
    data: paginatedData,
    itemsPerPage,
    setItemsPerPage,
    isLoading: false,
    prev: () => {
      if (validPage === 1) {
        return;
      }
      setPage(validPage - 1);
    },
    next: () => {
      if (validPage === totalPages) {
        return;
      }
      setPage(validPage + 1);
    },
  };
}
